// @ts-nocheck
import { resolveProcessGroup } from "@/pages/system/processes/processGrouping";
import React, { useState } from "react";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Printer,
  AlertOctagon,
  Layers,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PrintDialog from "@/components/shared/PrintDialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import { formatNumber } from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { getStatusConfig } from "@/config/status-registry";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import { EntityLink } from "@/components/shared/EntityLink";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import {
  StageDefectSelectorTable,
  normalizeDefectTallyItems,
} from "@/components/quality/StageDefectSelectorTable";
import {
  QcCategorizedMediaSection,
  normalizeCategorizedAttachments,
} from "@/components/quality/QcCategorizedMediaSection";
import { IpqcRoundsTable, IpqcStageBar, frequencyLabel, normalizePatrolLogs } from "@/components/quality/IpqcThreeStageSection";
import { CriteriaTable, LegacyMeasurements, QcSamplingSummary, VerdictSummary, checklistItemsOf } from "@/components/quality/qc-form";
import { isIpqcNewFlow, legacyParamRows, repeatColumnsOf } from "@/components/quality/qc-form/ipqc";
import { formatVnDateTime, planQcSampling } from "@apv-erp/status";
import { DetailField as F } from "@/components/shared/DetailField";

const SETUP_LABEL: Record<string, string> = { PASSED: "Đạt", ADJUST_REQUIRED: "Chỉnh máy", FAILED: "Không đạt" };
const SHIFT_LABEL: Record<string, string> = { CA_1: "Ca 1", CA_2: "Ca 2", CA_3: "Ca 3", HC: "Hành chính" };


export default function QCProcessDetailPage() {
  const navigate = useNavigate();
  const { checkId } = useParams({ strict: false }) as { checkId: string };

  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const queryClient = useQueryClient();
  const { workflow } = useWorkflowSteps("qcProcess");

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      return (trpcClient as any).qcProcess.updateStatus.mutate({
        id: checkId,
        status: newStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qcProcess", checkId] });
      queryClient.invalidateQueries({ queryKey: ["qcProcess"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Có lỗi xảy ra khi cập nhật trạng thái");
    },
  });

  const handleUpdateStatus = async (newStatus: string) => {
    await updateStatusMutation.mutateAsync(newStatus);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await (trpcClient as any).qcProcess.delete.mutate({ id: checkId });
      navigate({ to: "/qc-process" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  const {
    data: qc,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["qcProcess", checkId],
    queryFn: async () =>
      (trpcClient as any).qcProcess.getById.query({ id: checkId }),
  });

  const d = qc as any;

  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "QC Công đoạn", to: "/qc-process" }]}
        entityLabel="Phiếu QC công đoạn"
        backTo="/qc-process"
        codeOrId={checkId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Công đoạn", to: "/qc-process" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (error) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Công đoạn", to: "/qc-process" }]}
        title="Lỗi"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <Button onClick={() => navigate({ to: "/qc-process" })}>
            Quay lại
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  const sts = getStatusConfig("QCStatus", d.status);
  const isNonConforming =
    d.status === "FAILED" ||
    d.status === "WARNING" ||
    d.status === "REWORK" ||
    d.status === "fail";

  const newFlow = isIpqcNewFlow(d);
  const fourMItems: any[] = newFlow ? checklistItemsOf(d.fourMChecklist) : [];
  const checklistItems: any[] = checklistItemsOf(d.checklist);
  const rounds = normalizePatrolLogs(d.patrolLogs);
  const repeatColumns = repeatColumnsOf(fourMItems, checklistItems);
  const legacyRows = newFlow ? [] : legacyParamRows(d.technicalParams);
  const counts = { critical: d.criticalDefects || 0, major: d.majorDefects || 0, minor: d.minorDefects || 0 };
  const sampled = d.qtySample ?? d.sampleSize ?? 0;
  const lotForPlan = Number(d.lotSize || 0);
  const plannedSample =
    lotForPlan > 0 && d.reMajor
      ? planQcSampling({ lotSize: lotForPlan, mode: d.inspectionMode, level: d.aqlLevel, aqlMajor: Number(d.aqlMajor) || 1.5, aqlMinor: d.aqlMinor ?? null }).sampleSize
      : sampled;
  const setupLabel = d.setupResult ? SETUP_LABEL[d.setupResult] || d.setupResult : "Chưa duyệt";

  const handleCreateNcrLinkToForm = () => {
    navigate({
      to: "/ncr/create",
      search: {
        sourceType: "IPQC",
        sourceId: d.id,
      } as any,
    });
  };

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Kiểm tra công đoạn (IPQC)", to: "/qc-process" }]}
        title={d.processId}
        entityId={d.id}
        entityType="qc_process"
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={
          <Badge variant="outline" className={sts.color}>
            {sts.label || d.status}
          </Badge>
        }
        actions={
          <div className="flex gap-1.5 flex-wrap">
            {isNonConforming && (
              <Button
                variant="default"
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                onClick={handleCreateNcrLinkToForm}
              >
                <AlertOctagon size={14} className="mr-1.5" /> Lập NCR
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowPrint(true)} className="gap-1.5">
              <Printer size={14} /> In phiếu
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/qc-process/$checkId/edit" as any,
                  params: { checkId } as any,
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/qc-process" })}
        workflow={
          <WorkflowStepper
            workflow={workflow}
            currentStatus={d.status}
            onStatusChange={handleUpdateStatus}
            disabled={updateStatusMutation.isPending}
          />
        }
      >
        <div className="space-y-4">
          {isNonConforming && (
            <div className="rounded-xl border border-rose-300 bg-rose-50/90 p-3.5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <AlertOctagon className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="text-xs text-rose-800">
                  Công đoạn <strong>{d.processStep}</strong> của lệnh{" "}
                  <EntityLink entityType="productionOrder" id={d.productionOrderId || d.productionOrder?.id} code={d.productionOrder?.orderId} />{" "}
                  có kết quả{" "}
                  <strong>{sts.label || d.status}</strong>. Lập NCR để cách ly bán thành phẩm lỗi và xử lý.
                </div>
              </div>
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shrink-0" onClick={handleCreateNcrLinkToForm}>
                <AlertOctagon className="w-3.5 h-3.5 mr-1.5" />
                Lập NCR
              </Button>
            </div>
          )}

          {/* Thông tin chung */}
          <div className="border rounded-xl bg-white shadow-2xs p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-800">Thông tin chung</span>
              </div>
              <IpqcStageBar
                newFlow={newFlow}
                reportStage={d.reportStage}
                setupDone={d.setupResult === "PASSED"}
                signed={Boolean(d.sampleApprovedAt)}
                released={Boolean(d.releasedAt)}
                roundCount={rounds.length}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2.5">
              <F
                label="Lệnh SX"
                value={
                  <EntityLink
                    entityType="productionOrder"
                    id={d.productionOrder?.id || d.productionOrderId}
                    code={d.productionOrder?.orderId}
                    label={d.productionOrder?.productName}
                    variant="badge"
                  />
                }
              />
              <F
                label="Công đoạn"
                value={
                  d.stageProcessId ? (
                    <EntityLink entityType="process" id={d.stageProcessId} label={d.processStep || d.stageProcessId} />
                  ) : d.processStep ? (
                    // tên giai đoạn gộp (VD "Bế / Ép kim / Dập nổi"): mở danh sách công đoạn của nhóm đó
                    <Link
                      to="/processes"
                      search={{ group: resolveProcessGroup({ name: String(d.processStep).replace(/^\s*\d+\s*[.)-]\s*/, "") }) } as any}
                      className="text-indigo-600 hover:underline"
                    >
                      {d.processStep}
                    </Link>
                  ) : undefined
                }
              />
              <F
                label="Máy"
                value={
                  d.machine ? (
                    <EntityLink entityType="machine" id={d.machine.id} code={d.machine.machineId} label={d.machine.name} variant="badge" />
                  ) : (
                    d.machineId || "—"
                  )
                }
              />
              <F label="Ca" value={SHIFT_LABEL[d.shift] || d.shift} />
              <F label="Thợ đứng máy" value={d.operatorName} />
              <F label="Người kiểm" value={d.inspector?.fullName || d.inspector?.email} />
              <F
                label="Sản phẩm"
                value={
                  d.product || d.productionOrder?.product ? (
                    <EntityLink
                      entityType="product"
                      id={(d.product || d.productionOrder?.product)?.id}
                      code={(d.product || d.productionOrder?.product)?.productCode}
                      label={(d.product || d.productionOrder?.product)?.productName}
                    />
                  ) : (
                    d.productionOrder?.productName
                  )
                }
              />
              <F
                label="Khách hàng"
                value={
                  d.customer || d.productionOrder?.customer ? (
                    <EntityLink
                      entityType="customer"
                      id={(d.customer || d.productionOrder?.customer)?.id}
                      code={(d.customer || d.productionOrder?.customer)?.code}
                      label={(d.customer || d.productionOrder?.customer)?.name}
                    />
                  ) : null
                }
              />
              <F label="Mẫu chuẩn" value={d.referenceSampleNo} />
              <F label="Tần suất" value={frequencyLabel(d.sampleFrequency)} />
              <F
                label="Mẫu kiểm tra"
                value={
                  d.template
                    ? `${d.template.code}${d.template.version ? ` v${d.template.version}` : ""}${d.template.status === "ARCHIVED" ? " · Lưu trữ" : ""}`
                    : null
                }
              />
            </div>
            <div className="border-t border-slate-100 pt-3">
              <QcSamplingSummary
                lotSize={d.lotSize ?? d.productionOrder?.qtyPlan}
                mode={d.inspectionMode}
                level={d.aqlLevel}
                aqlMajor={d.aqlMajor}
                aqlMinor={d.aqlMinor}
                sampleSize={plannedSample}
                acMajor={d.acMajor}
                reMajor={d.reMajor}
                acMinor={d.acMinor}
                reMinor={d.reMinor}
              />
            </div>
          </div>

          {newFlow ? (
            <>
              <CriteriaTable title="1. Duyệt setup (4M)" items={fourMItems} readOnly toolbar={<Badge variant="outline" className="text-[11px] font-bold">Setup: {setupLabel}</Badge>} />
              <div className="rounded-xl border border-violet-200 bg-white shadow-2xs overflow-hidden" data-testid="ipqc-signoff">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-violet-200 bg-violet-50/70 px-3.5 py-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-800">2. Ký mẫu</span>
                  <span className="text-[11px] font-semibold text-violet-800" data-testid="ipqc-signed">
                    {d.sampleApprovedAt
                      ? `Đã ký ${formatVnDateTime(d.sampleApprovedAt)}${d.sampleApprovedByName ? ` · ${d.sampleApprovedByName}` : ""}`
                      : "Chưa ký mẫu"}
                  </span>
                </div>
                <div className="space-y-3 p-3">
                  <CriteriaTable title="Chỉ tiêu sản phẩm" items={checklistItems} readOnly emptyText="Không có chỉ tiêu sản phẩm." />
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4 text-xs">
                    <F label="Số mẫu ký" value={`${d.setupSampleQty ?? 0} (lỗi ${d.setupDefectQty ?? 0})`} />
                    <F label="Số mẫu lưu" value={d.sampleRef} />
                    <F label="Trưởng ca" value={d.sampleShiftLead} />
                    <F
                      label="Cho sản xuất"
                      value={d.releasedAt ? `${formatVnDateTime(d.releasedAt)}${d.releasedByName ? ` · ${d.releasedByName}` : ""}` : "Chưa"}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="border rounded-xl bg-white shadow-2xs p-4 grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
                <F label="Duyệt setup" value={setupLabel} />
                <F label="Mẫu setup" value={`${d.setupSampleQty ?? 5} (lỗi ${d.setupDefectQty ?? 0})`} />
                <F label="Ghi chú setup" value={d.setupNotes} />
              </div>
              <CriteriaTable items={checklistItems} readOnly emptyText="Phiếu chưa ghi chỉ tiêu." />
              <LegacyMeasurements rows={legacyRows} />
            </>
          )}

          {(rounds.length > 0 || newFlow) && (
            <div className="rounded-xl border border-amber-200 bg-white shadow-2xs overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50/70 px-3.5 py-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  {newFlow ? "3. Kiểm tra lặp lại" : "Kiểm tra lặp lại"} ({rounds.length} lần)
                </span>
                {d.sampleFrequency ? <span className="text-[11px] text-slate-600">Tần suất: {frequencyLabel(d.sampleFrequency)}</span> : null}
              </div>
              <div className="p-3">
                <IpqcRoundsTable rounds={rounds} columns={repeatColumns} processStage={d.processStage} legacy={!newFlow} />
              </div>
            </div>
          )}

          {normalizeDefectTallyItems(d.defectDetails).length > 0 && (
            <StageDefectSelectorTable
              processStage={d.processStage || "PRINTING"}
              lotSize={d.lotSize || 0}
              sampleSize={sampled}
              defectDetails={d.defectDetails}
              onChange={() => {}}
              readOnly
              hideSampleSummary
            />
          )}

          <VerdictSummary
            status={d.status}
            counts={counts}
            sampleSize={sampled}
            reMajor={d.reMajor}
            reMinor={d.reMinor}
            overrideReason={d.overrideReason}
          >
            <div className="grid grid-cols-2 gap-2 border-t border-slate-100 pt-2 text-xs md:grid-cols-4" data-testid="ipqc-lot-summary">
              <F label="Cỡ lô" value={formatNumber(d.lotSize || d.productionOrder?.qtyPlan || 0)} />
              <F label="Tổng mẫu" value={formatNumber(sampled)} />
              <F label="Mẫu lỗi" value={formatNumber(d.qtyDefect ?? 0)} />
              <F label="Số lần kiểm" value={rounds.length} />
            </div>
            {d.notes ? (
              <div className="text-xs text-slate-700">
                <b>Nhận xét:</b> <span className="whitespace-pre-wrap">{d.notes}</span>
              </div>
            ) : null}
          </VerdictSummary>

          {normalizeCategorizedAttachments(d.attachments, "QC").length > 0 && (
            <QcCategorizedMediaSection mode="QC" value={d.attachments} onChange={() => {}} readOnly />
          )}

          {Array.isArray(d.ncrs) && d.ncrs.length > 0 && (
            <div className="border border-rose-200 rounded-xl bg-white shadow-2xs overflow-hidden">
              <div className="px-4 py-2.5 border-b border-rose-100 bg-rose-50/60 flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-rose-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-rose-900">NCR liên kết ({d.ncrs.length})</span>
              </div>
              <div className="divide-y divide-slate-100">
                {d.ncrs.map((ncr: any) => (
                  <div
                    key={ncr.id}
                    className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate({ to: `/ncr/${ncr.id}` as any })}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-rose-700 text-xs">{ncr.ncrId}</span>
                      <span className="text-xs font-medium text-slate-800">{ncr.issueSummary}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {ncr.disposition && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                          {ncr.disposition}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {ncr.status}
                      </Badge>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </StandardDetailLayout>

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title={`Xoá QC công đoạn ${d.processId}?`}
        description="Hành động này không thể hoàn tác."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />

      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type="qc_process"
        data={d}
      />
    </>
  );
}
